import { useRef } from "react";
import { motion } from "framer-motion";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import useContactForm from "./../../hook/useContactForm";
import sendViaWhatsApp from './../../utils/sendViaWhatsApp';

const WHATSAPP_NUMBER = "+918178052636";
const PHONE_DISPLAY = "8178052636";

export default function BookHero() {
    const sectionRef = useRef(null);
    const { formData, errors, handleChange, handleSubmit } = useContactForm();

    return (
        <section
            id="home"
            ref={sectionRef}
            className="relative w-full min-h-screen overflow-hidden bg-black py-20 sm:py-24"
        >
            {/* Background Video */}
            <video
                autoPlay
                loop
                muted
                playsInline
                className="absolute top-0 left-0 w-full h-full object-cover z-0 block"
                preload="auto"
            >
                <source src="/assets/hero-bg.mp4" type="video/mp4" />
                Your browser does not support the video tag.
            </video>

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/60 z-10" />

            {/* Content */}
            <div className="relative z-20 flex items-center justify-center px-4 mt-9">
                <motion.div className="w-full max-w-[90rem] bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 md:p-8 shadow-2xl text-white">
                    <div className="grid md:grid-cols-2 gap-8">
                        <motion.div
                            initial={{ x: -50, opacity: 0 }}
                            whileInView={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.8 }}
                            className="flex flex-col justify-center h-full space-y-6"
                        >
                            <p className="text-yellow-400 text-sm sm:text-base font-medium uppercase tracking-widest">
                                For All Your Devices — No Visiting charges, Fast Response
                            </p>
                            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-white">
                                <span className="relative inline-block">
                                    Skip the Hassle - Book{' '}
                                    <span className="text-yellow-400 underline underline-offset-4 decoration-4 decoration-yellow-400">
                                        Home Services
                                    </span>{' '}
                                    Anywhere in Delhi NCR
                                </span>

                            </h1>
                            <p className="text-base sm:text-xl text-white/80 max-w-md">
                                Book in seconds with no visiting charges, no hidden fees, and instant confirmation — right from your phone.


                            </p>

                            {/* Contact Info */}
                            <div className="flex items-center gap-2 text-base sm:text-lg font-semibold text-white">
                                <PhoneIcon fontSize="small" />{' '}
                                <a
                                    href={`tel:${WHATSAPP_NUMBER}`}
                                    className="hover:text-yellow-400"
                                >
                                    {PHONE_DISPLAY}
                                </a>
                            </div>

                            <div>
                                <button
                                    onClick={() =>
                                        window.open(
                                            `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                                                "Hi, I want to book a home service appointment."
                                            )}`,
                                            "_blank",
                                            "noopener,noreferrer"
                                        )
                                    }
                                    className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2 px-4 rounded flex items-center gap-2"
                                    aria-label="Chat on WhatsApp"
                                >
                                    <WhatsAppIcon />
                                    Chat on WhatsApp
                                </button>

                                <noscript>
                                    <a
                                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                                            "Hi, I want to book a home service appointment."
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-yellow-400 underline mt-2 block"
                                    >
                                        Chat on WhatsApp
                                    </a>
                                </noscript>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ x: 50, opacity: 0 }}
                            whileInView={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.8 }}
                        >
                            <form
                                onSubmit={handleSubmit((data) => {
                                    console.log(data);
                                    alert("Redirecting to WhatsApp...");
                                    sendViaWhatsApp(data);
                                })}
                                className="text-white"
                            >
                                <h2 className="text-lg text-center sm:text-2xl font-bold mb-4">
                                    Request a Booking
                                </h2>
                                <p className="text-xs text-center sm:text-sm mb-4">
                                    Enter your details, and we’ll contact you within the next{' '}
                                    <span className="text-yellow-400 underline underline-offset-2 decoration-1 decoration-yellow-400">
                                        60 minutes!
                                    </span>
                                </p>
                                <div className="space-y-4">
                                    {["name", "phone", "email", "message"].map((field) => (
                                        <TextField
                                            key={field}
                                            label={
                                                field === "message"
                                                    ? "Service Details or Questions"
                                                    : field.charAt(0).toUpperCase() + field.slice(1)
                                            }
                                            name={field}
                                            value={formData[field]}
                                            onChange={handleChange}
                                            required={field === "name" || field === "phone"}
                                            fullWidth
                                            variant="outlined"
                                            multiline={field === "message"}
                                            rows={field === "message" ? 3 : undefined}
                                            error={!!errors[field]}
                                            helperText={errors[field] || ""}
                                            InputLabelProps={{ style: { color: "#facc15" } }}
                                            InputProps={{
                                                style: { color: "#fff" },
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { borderColor: "#facc15" },
                                                    "&:hover fieldset": { borderColor: "#facc15" },
                                                    "&.Mui-focused fieldset": { borderColor: "#facc15" },
                                                },
                                                "& label.Mui-focused": { color: "#facc15" },
                                                "& input:-webkit-autofill": {
                                                    WebkitTextFillColor: "#fff !important",
                                                },

                                            }}
                                        />
                                    ))}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                            backgroundColor: "#facc15",
                                            color: "#000",
                                            "&:hover": { backgroundColor: "#eab308" },
                                        }}
                                    >
                                        Submit
                                    </Button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}